import { Link, NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LayoutDashboard, CalendarDays, Bell, Search, LogOut, Menu, X, ExternalLink, MailCheck, ShieldCheck, LifeBuoy } from "lucide-react";
import { useState } from "react";
import LanguageSwitcher from "./LanguageSwitcher";
import InstallAppButton from "./InstallAppButton";
import NoIndex from "./NoIndex";
import { BrandLogo } from "./BrandLogo";
import useAccessibleOverlay from "../hooks/useAccessibleOverlay";

const navItems = [
  { to: "/portal", label: "My Bookings", icon: CalendarDays, end: true, testId: "portal-nav-bookings" },
  { to: "/portal/find", label: "Find a Business", icon: Search, testId: "portal-nav-find" },
  { to: "/portal/notifications", label: "Notifications", icon: Bell, testId: "portal-nav-notifications" },
  { to: "/portal/privacy", label: "Privacy", icon: ShieldCheck, testId: "portal-nav-privacy" },
  { to: "/portal/support", label: "Help & Support", icon: LifeBuoy, testId: "portal-nav-support" },
];

export default function CustomerLayout() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { panelRef, triggerRef } = useAccessibleOverlay(open, () => setOpen(false));
  const [sendingVerification, setSendingVerification] = useState(false);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if ((user.account_type || user.role) !== "customer") return <Navigate to="/app" replace />;

  const handleLogout = () => { logout(); navigate("/"); };
  const resendVerification = async () => {
    setSendingVerification(true);
    try {
      await api.post("/auth/send-verification-email");
      toast.success("Verification email sent. Check your inbox.");
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || "Could not send verification email");
    } finally {
      setSendingVerification(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-slate-900">
      <NoIndex title="Customer Portal · ConfirmPH" />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="flex items-center justify-between px-4 sm:px-6 h-16">
          <div className="flex items-center gap-3">
            <button data-testid="portal-sidebar-toggle" ref={triggerRef} aria-label="Open customer navigation"
                    aria-expanded={open} aria-controls="customer-mobile-navigation"
                    className="lg:hidden min-w-11 min-h-11 p-2 -ml-2 rounded-md hover:bg-slate-100" onClick={() => setOpen(!open)}>
              <Menu className="w-5 h-5" aria-hidden="true" />
            </button>
            <Link to="/portal" className="flex items-center gap-2.5">
              <BrandLogo
                variant="full"
                className="h-9 w-auto"
                alt="ConfirmPH"
                testId="portal-brand-logo"
              />
              <div className="hidden sm:block leading-tight border-l border-slate-200 pl-2.5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Customer</div>
                <div className="text-sm font-medium text-slate-700">Portal</div>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-slate-900">{user?.name}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
            <Link to="/" data-testid="portal-view-site"
                   className="hidden sm:inline-flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-slate-100 text-xs text-slate-600">
              <ExternalLink className="w-3.5 h-3.5" /> View site
            </Link>
            <button data-testid="portal-logout-btn" onClick={handleLogout}
                    className="p-2 rounded-md hover:bg-slate-100 text-slate-700" title="Log out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {open && <div aria-hidden="true" data-overlay-backdrop className="fixed inset-0 top-16 z-20 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
        <aside ref={panelRef} id="customer-mobile-navigation" aria-label="Customer navigation" tabIndex={open ? -1 : undefined}
               role={open ? "dialog" : undefined} aria-modal={open ? "true" : undefined}
               className={`${open ? "block" : "hidden"} lg:block fixed lg:sticky top-16 lg:top-16 left-0 z-30 w-64 h-[calc(100vh-4rem)] bg-white border-r border-slate-200 overflow-y-auto`}>
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2 lg:hidden">
            <span className="font-semibold">Customer menu</span>
            <button type="button" data-autofocus aria-label="Close customer navigation" onClick={() => setOpen(false)} className="grid min-h-11 min-w-11 place-items-center rounded-md hover:bg-slate-100"><X className="h-5 w-5" aria-hidden="true" /></button>
          </div>
          <nav className="p-3 space-y-1">
            <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">
              <LayoutDashboard className="w-3 h-3 inline mr-1 mb-0.5" />
              My account
            </div>
            {navItems.map(({ to, label, icon: Icon, end, testId }) => (
              <NavLink
                key={to} to={to} end={end} data-testid={testId} onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive ? "bg-[#1B4332] text-white" : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.8} />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 mt-2 border-t border-slate-200">
            <p className="text-xs text-slate-500 leading-relaxed">
              Your bookings appear here automatically when a business creates one using your email.
            </p>
            <div className="mt-3">
              <InstallAppButton
                variant="secondary"
                size="sm"
                className="w-full justify-center"
                testId="portal-install-app-btn"
                label="Install ConfirmPH"
              />
            </div>
          </div>
        </aside>

        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
          {!user.email_verified && (
            <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                 data-testid="portal-email-verification-required">
              <MailCheck className="w-5 h-5 text-amber-700 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-amber-950">Verify your email to view private booking history</p>
                <p className="text-sm text-amber-800">This protects bookings already associated with {user.email}.</p>
              </div>
              <button type="button" onClick={resendVerification} disabled={sendingVerification}
                      className="rounded-lg bg-amber-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {sendingVerification ? "Sending…" : "Resend verification"}
              </button>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
